/**
 * Company workspace — Attempts Dashboard (Story 48).
 *
 * Hiring managers see all Attempts under their Company's quota, filtered by
 * test, date range. Cursor pagination with "Load more" button.
 *
 * Consumes shared `<AttemptsTable>` with `role="manager"`.
 * Columns: Candidate, Test, Date, Duration, Score, Result.
 * No Company column (all rows are this company).
 *
 * Issue 15-frontend-gaps/15 — unified attempts-list schema.
 */

import { useState, useCallback, useEffect, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Shell } from '../../components/shell/Shell.tsx';
import { Button, Input } from '@/components/ui';
import { AttemptsTable } from '../../components/attempts/AttemptsTable.tsx';
import { AttemptsSummaryCards } from '../../components/attempts/AttemptsSummaryCards.tsx';
import type { AttemptsTableRow } from '../../components/attempts/types.ts';
import { apiFetch } from '@/lib/api-client';

/** Download glyph — SVG asset. */
function DownloadGlyph(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      width="14"
      height="14"
      className="mr-2 shrink-0"
    >
      <path
        d="M8 2v8m0 0L4.5 6.5M8 10l3.5-3.5M3 13h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface AttemptSummary {
  attempt_id: string;
  test_id: string;
  test_title: string;
  track: string;
  level: string;
  user_id: string | null;
  candidate_name: string;
  candidate_email: string;
  score: number;
  passed: boolean;
  status: string;
  completed_at: string | null;
  duration_seconds: number | null;
  is_embed_attempt: boolean;
}

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

/** Map the company API response shape to the canonical row shape. */
function toRow(a: AttemptSummary): AttemptsTableRow {
  return {
    attempt_id: a.attempt_id,
    candidate_name: a.candidate_name,
    candidate_email: a.candidate_email,
    test_title: a.test_title,
    company_name: null, // always this company — hidden column
    date: a.completed_at ?? '',
    duration_seconds: a.duration_seconds,
    score_percentage: a.score,
    passed: a.passed,
  };
}

export function AttemptsPage(): ReactElement {
  const params = useParams<{ id: string }>();
  const companyId = params.id ?? '';

  const [attempts, setAttempts] = useState<AttemptSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [testIdFilter, setTestIdFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const loadAttempts = useCallback(
    async (nextCursor?: string | null) => {
      nextCursor ? setIsLoadingMore(true) : setIsLoading(true);
      setError(null);

      try {
        const q = new URLSearchParams();
        if (nextCursor) q.set('cursor', nextCursor);
        if (testIdFilter) q.set('test_id', testIdFilter);
        if (dateFrom) q.set('date_from', dateFrom);
        if (dateTo) q.set('date_to', dateTo);
        q.set('limit', '25');

        const response = await apiFetch(
          `/api/companies/${companyId}/attempts?${q.toString()}`,
          { credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to load attempts: ${response.statusText}`,
          );
        }

        const data = (await response.json()) as {
          ok: boolean;
          data: Page<AttemptSummary>;
        };
        if (!data.ok) {
          throw new Error('API returned error');
        }

        if (nextCursor) {
          setAttempts((prev) => [...prev, ...data.data.items]);
        } else {
          setAttempts(data.data.items);
        }

        setCursor(data.data.next_cursor);
        setHasMore(!!data.data.next_cursor);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Unknown error',
        );
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [companyId, testIdFilter, dateFrom, dateTo],
  );

  useEffect(() => {
    void loadAttempts();
  }, [loadAttempts]);

  const handleApplyFilters = (): void => {
    setCursor(null);
    setAttempts([]);
    void loadAttempts();
  };

  // J-C8: CSV export — fires GET with format=csv and current filters
  const handleExportCsv = (): void => {
    const q = new URLSearchParams();
    q.set('format', 'csv');
    if (testIdFilter) q.set('test_id', testIdFilter);
    if (dateFrom) q.set('date_from', dateFrom);
    if (dateTo) q.set('date_to', dateTo);
    window.open(`/api/companies/${companyId}/attempts?${q.toString()}`, '_blank');
  };

  const rows = attempts.map(toRow);

  // Summary stats
  const completedRows = rows.filter((r) => r.score_percentage !== null);
  const passedCount = completedRows.filter((r) => r.passed).length;
  const avgScore =
    completedRows.length > 0
      ? completedRows.reduce(
          (s, r) => s + (r.score_percentage ?? 0),
          0,
        ) / completedRows.length
      : 0;

  return (
    <Shell mode="company" densityMode="workspace" companyId={companyId}>
      <header className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-mono text-muted-2">
              COMPANY · ATTEMPTS
            </p>
            <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">
              Attempts Dashboard
            </h1>
            <p className="mt-2 max-w-prose font-sans text-body text-muted">
              View all candidate attempts under your Company's quota.
            </p>
          </div>
          <Button variant="secondary" type="button" onClick={handleExportCsv}>
            <DownloadGlyph />
            Export CSV
          </Button>
        </div>
      </header>

      {/* Summary cards */}
        {rows.length > 0 && (
          <AttemptsSummaryCards
            role="manager"
            totalAttempts={rows.length}
            passedCount={passedCount}
            averageScore={avgScore}
          />
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* Filter sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-4 space-y-6 rounded border border-muted/20 bg-card p-4">
              <div>
                <label
                  htmlFor="test-filter"
                  className="mb-2 block font-sans text-label font-medium text-ink"
                >
                  Test
                </label>
                <select
                  id="test-filter"
                  value={testIdFilter}
                  onChange={(e) => setTestIdFilter(e.target.value)}
                  className="w-full rounded border border-muted/30 bg-bg px-3 py-2 font-sans text-body text-ink"
                >
                  <option value="">All tests</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="date-from"
                  className="mb-2 block font-sans text-label font-medium text-ink"
                >
                  From date
                </label>
                <Input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full rounded border border-muted/30 bg-bg px-3 py-2 font-sans text-body text-ink"
                />
              </div>

              <div>
                <label
                  htmlFor="date-to"
                  className="mb-2 block font-sans text-label font-medium text-ink"
                >
                  To date
                </label>
                <Input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full rounded border border-muted/30 bg-bg px-3 py-2 font-sans text-body text-ink"
                />
              </div>

              <Button variant="primary" type="button" onClick={handleApplyFilters} className="w-full">
                Apply Filters
              </Button>
            </div>
          </aside>

          {/* Main content */}
          <section className="lg:col-span-3">
            <AttemptsTable
              role="manager"
              rows={rows}
              loading={isLoading}
              loadingMore={isLoadingMore}
              hasMore={hasMore}
              onLoadMore={() => void loadAttempts(cursor)}
              error={error}
              onRetry={() => void loadAttempts()}
              errorFallbackHref={`/company/${companyId}/dashboard`}
              emptyState={{
                ctaLabel: 'Manage share tokens',
                ctaHref: `/company/${companyId}/share-tokens`,
              }}
            />
          </section>
        </div>
    </Shell>
  );
}
